"use client";

import { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  Skeleton,
  Fade,
  Divider,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  Send,
  Support as SupportIcon,
  ExpandMore,
  QuestionAnswer,
  ContactSupport,
  Schedule,
  CheckCircle,
} from "@mui/icons-material";

const Support = () => {
  const [contactInfo, setContactInfo] = useState({ phone: "", email: "" });
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://namami-infotech.com/EvaraBackend/src/support/get_support.php"
        );
        const data = await response.json();
        if (data.success && data.data.length > 0) {
          const { phone_number, email } = data.data[0];
          setContactInfo({ phone: phone_number, email });
        }
      } catch (error) {
        console.error("Error fetching contact info:", error);
        setContactInfo({ phone: "Not available", email: "Not available" });
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setSubmitStatus("success");
      setFormData({ name: "", email: "", message: "" });
      setSubmitting(false);

      // Clear success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 2000);
  };

  const faqs = [
    {
      question: "How do I track my order?",
      answer:
        "You can track your order by logging into your account and visiting the 'Order History' section. You'll find real-time tracking information for all your orders.",
    },

    {
      question: "How long does delivery take?",
      answer:
        "Standard delivery takes 2-5 business days. Express delivery is available for urgent orders and takes 1-2 business days.",
    },
    {
      question: "Do you offer prescription services?",
      answer:
        "Yes, we provide prescription services. Upload your prescription during checkout, and our licensed pharmacists will verify it before processing your order.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards, debit cards, UPI, net banking, and cash on delivery for eligible orders.",
    },
  ];

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
        minHeight: "100vh",
        py: 6,
      }}
    >
      <Container maxWidth="xl">
        <Fade in timeout={800}>
          <Box textAlign="center" mb={6}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                backdropFilter: "blur(10px)",
              }}
            >
              <SupportIcon sx={{ fontSize: 40, color: "white" }} />
            </Avatar>
            <Typography
              variant="h3"
              sx={{
                color: "white",
                fontWeight: "bold",
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                mb: 2,
              }}
            >
              Support & Contact Us
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.9)",
                maxWidth: 600,
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              We're here to help! Get in touch with our support team or find
              answers to common questions.
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={4}>
          <Grid item xs={12} lg={6}>
            <Fade in timeout={1000}>
              <Card
                sx={{
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(20px)",
                  borderRadius: 3,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <ContactSupport
                      sx={{ color: "#FF6B35", mr: 2, fontSize: 28 }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ color: "#008080", fontWeight: "bold" }}
                    >
                      Send us a Message
                    </Typography>
                  </Box>

                  {submitStatus === "success" && (
                    <Alert
                      severity="success"
                      sx={{ mb: 3 }}
                      icon={<CheckCircle />}
                    >
                      Thank you! Your message has been sent successfully. We'll
                      get back to you soon.
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit}>
                    <TextField
                      name="name"
                      label="Full Name"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": { borderColor: "#008080" },
                          "&.Mui-focused fieldset": { borderColor: "#008080" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#008080",
                        },
                      }}
                    />
                    <TextField
                      name="email"
                      label="Email Address"
                      type="email"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": { borderColor: "#008080" },
                          "&.Mui-focused fieldset": { borderColor: "#008080" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#008080",
                        },
                      }}
                    />
                    <TextField
                      name="message"
                      label="Your Message"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={4}
                      margin="normal"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": { borderColor: "#008080" },
                          "&.Mui-focused fieldset": { borderColor: "#008080" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#008080",
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disabled={submitting}
                      startIcon={submitting ? null : <Send />}
                      sx={{
                        mt: 3,
                        py: 1.5,
                        background: "linear-gradient(45deg, #008080, #20B2AA)",
                        "&:hover": {
                          background:
                            "linear-gradient(45deg, #006666, #1a9999)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(0,128,128,0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Fade in timeout={1200}>
              <Card
                sx={{
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(20px)",
                  borderRadius: 3,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  mb: 3,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <LocationOn
                      sx={{ color: "#FF6B35", mr: 2, fontSize: 28 }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ color: "#008080", fontWeight: "bold" }}
                    >
                      Contact Information
                    </Typography>
                  </Box>

                  <Box mb={3}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Phone sx={{ color: "#008080", mr: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Phone Number
                        </Typography>
                        {loading ? (
                          <Skeleton width={150} />
                        ) : (
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {contactInfo.phone}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center" mb={2}>
                      <Email sx={{ color: "#008080", mr: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Email Address
                        </Typography>
                        {loading ? (
                          <Skeleton width={200} />
                        ) : (
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {contactInfo.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                      <Schedule sx={{ color: "#008080", mr: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Business Hours
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "medium" }}
                        >
                          Mon - Sat: 9:00 AM - 8:00 PM
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#008080", mb: 2, fontWeight: "bold" }}
                    >
                      Our Location
                    </Typography>
                    <Box
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    >
                      <iframe
                        title="Our Location"
                        src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3499.5258527973187!2d77.44695667550343!3d28.70382687562583!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjjCsDQyJzEzLjgiTiA3N8KwMjYnNTguMyJF!5e0!3m2!1sen!2sin!4v1729408130399!5m2!1sen!2sin"
                        width="100%"
                        height="250"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        </Grid>

        <Fade in timeout={1400}>
          <Box mt={6}>
            <Card
              sx={{
                background: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(20px)",
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" mb={4}>
                  <QuestionAnswer
                    sx={{ color: "#FF6B35", mr: 2, fontSize: 28 }}
                  />
                  <Typography
                    variant="h5"
                    sx={{ color: "#008080", fontWeight: "bold" }}
                  >
                    Frequently Asked Questions
                  </Typography>
                </Box>

                {faqs.map((faq, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      mb: 1,
                      "&:before": { display: "none" },
                      boxShadow: "none",
                      border: "1px solid rgba(0,128,128,0.1)",
                      borderRadius: "8px !important",
                      "&.Mui-expanded": {
                        boxShadow: "0 2px 8px rgba(0,128,128,0.1)",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: "#008080" }} />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                        },
                      }}
                    >
                      <Chip
                        label={`Q${index + 1}`}
                        size="small"
                        sx={{
                          bgcolor: "#008080",
                          color: "white",
                          mr: 2,
                          fontWeight: "bold",
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "medium" }}
                      >
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant="body1"
                        sx={{ color: "text.secondary", lineHeight: 1.6 }}
                      >
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Support;
